<template>
	<div class="bg-white border rounded-lg p-4">
		<div class="mb-4">
			<h6 class="text-lg uppercase tracking-wider text-gray-500 mb-1">
				Ajustari modele
			</h6>
			<v-label class="text-base">
				Ajustare procentuala aplicata peste pretul de baza, pe model.
				0% = fara ajustare, valorile negative scad pretul.
				<span v-if="hasGeometry" class="block">
					Lamelele sunt latimile in mm care se repeta pe modul (60 / 100 / 20), iar golul minim e
					distanta cea mai mica admisa intre ele: 0 inseamna lamele lipite, iar o valoare
					negativa lamele suprapuse. Ofertele calculeaza numarul de lamele din ele.
				</span>
			</v-label>
		</div>

		<v-form v-model="validForm" ref="formModelAdjustments" @submit.prevent="save">
			<!-- HUB's own pricing carries the model list and its geometry; the configurator keeps
			     its geometry on its side, so there only the markup is editable -->
			<table v-if="hasGeometry" class="w-full max-w-3xl mb-4">
				<thead>
					<tr class="text-left text-gray-500">
						<th class="pb-2 w-4/12">Model</th>
						<th class="pb-2 w-2/12">Ajustare</th>
						<th class="pb-2 w-4/12">Lamele</th>
						<th class="pb-2 w-2/12">Gol minim</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="model in models" :key="model">
						<td class="py-1 pr-2 align-middle">{{ model }}</td>
						<td class="py-1 pr-2 align-middle">
							<v-text-field v-model="adjustments[model]" dense type="number" step="1" hide-spin-buttons
								outlined suffix="%" hide-details="auto"
								:rules="[rules.required, rules.adjustment]"></v-text-field>
						</td>
						<td class="py-1 pr-2 align-middle">
							<v-text-field v-model="slatWidths[model]" dense outlined suffix="mm" hide-details="auto"
								placeholder="60 / 100 / 20" :rules="[rules.required, rules.widths]"></v-text-field>
						</td>
						<td class="py-1 pr-2 align-middle">
							<v-text-field v-model="minGaps[model]" dense type="number" step="1"
								hide-spin-buttons outlined suffix="mm" hide-details="auto"
								:rules="[rules.required, rules.number]"></v-text-field>
						</td>
					</tr>
				</tbody>
			</table>

			<div v-else class="flex flex-wrap gap-2 mb-4">
				<v-text-field v-for="model in models" :key="model" v-model="adjustments[model]"
					class="w-40 flex-grow-0" dense type="number" step="1" hide-spin-buttons outlined :label="model"
					suffix="%" hide-details="auto" :rules="[rules.required, rules.adjustment]"></v-text-field>
			</div>

			<div class="flex justify-end">
				<v-btn type="submit" color="teal" class="uppercase white--text" :loading="saving"
					:disabled="saving || !hasChanges">Salveaza</v-btn>
			</div>
		</v-form>
	</div>
</template>

<script>
import pricingSection from './pricingSection';

// Fallback list for a store that has no model list of its own - the configurator's, whose source
// of truth is ALL_FENCE_MODELS in its config/pricing.js. HUB's own pricing serves the list from
// the API instead, so a model added there shows up here without a deploy. Any model the
// adjustments endpoint returns that is missing here is still shown.
const FENCE_MODELS = [
	'H60/100/20', 'H60', 'H80', 'H100', 'H105', 'H120', 'H150', 'H200',
	'H87', 'H90', 'H125', 'H87J', 'V20', 'V30R', 'V60', 'V87JR',
	'V87J', 'H150/20', 'H150/20I', 'V87R'
];

/**
 * "60 / 100 / 20" -> [60, 100, 20]
 *
 * Only a slash or a space separates widths. A comma deliberately does not: a slat can be 87.4 mm,
 * and someone typing that the Romanian way as "87,4" would otherwise get two slats of 87 and 4
 * without a word of complaint. Read as a decimal comma it is not a number, so the rule below
 * rejects it and the entry gets fixed instead of quietly becoming a different fence.
 */
function parseWidths(value) {
	return String(value == null ? '' : value)
		.split(/[/\s]+/)
		.filter(part => part !== '')
		.map(part => Number(part));
}

export default {
	name: "ModelAdjustments",
	mixins: [pricingSection],
	data() {
		return {
			validForm: false,
			adjustments: {},
			slatWidths: {},
			minGaps: {},
			rules: {
				required: value => (value !== '' && value !== null && value !== undefined) || 'Câmp obligatoriu',
				adjustment: value => (!isNaN(parseFloat(value)) && parseFloat(value) > -100) || 'Valoare mai mare ca -100',
				// the min gap is signed on purpose: 0 = slats touch, negative = slats overlap
				number: value => !isNaN(parseFloat(value)) || 'Valoare invalida',
				widths: value => {
					const widths = parseWidths(value);
					return (widths.length > 0 && widths.every(width => width > 0))
						|| 'Latimi separate prin / , zecimale cu punct';
				},
			},
		};
	},
	computed: {
		saving() {
			return this.section.savingModelAdjustments || this.section.savingModels;
		},
		modelAdjustments() {
			return this.section.modelAdjustments;
		},
		/** [{ code, name, slatWidths, minGap }], empty on a store that has no model list */
		storedModels() {
			return this.section.models;
		},
		hasGeometry() {
			return this.storedModels.length > 0;
		},
		geometryByCode() {
			const geometry = {};
			this.storedModels.forEach(model => { geometry[model.code] = model });
			return geometry;
		},
		models() {
			const known = this.hasGeometry ? this.storedModels.map(model => model.code) : FENCE_MODELS;
			const extra = Object.keys(this.modelAdjustments).filter(model => !known.includes(model));

			return known.concat(extra);
		},
		hasChanges() {
			return Object.keys(this.buildChanges()).length > 0
				|| Object.keys(this.buildGeometryChanges()).length > 0;
		}
	},
	methods: {
		// the API only stores the models that have a markup, everything else is 0
		storedPercent(model) {
			const stored = this.modelAdjustments[model];
			return stored === null || stored === undefined ? 0 : Math.round(Number(stored) * 10000) / 100;
		},
		storedWidths(model) {
			const stored = this.geometryByCode[model];
			return stored && stored.slatWidths ? stored.slatWidths : [];
		},
		storedMinGap(model) {
			const stored = this.geometryByCode[model];
			return stored ? Number(stored.minGap) : 0;
		},
		syncFromStore() {
			const adjustments = {};
			const slatWidths = {};
			const minGaps = {};

			this.models.forEach(model => {
				adjustments[model] = this.storedPercent(model);
				slatWidths[model] = this.storedWidths(model).join(' / ');
				minGaps[model] = this.storedMinGap(model);
			});

			this.adjustments = adjustments;
			this.slatWidths = slatWidths;
			this.minGaps = minGaps;
		},
		buildChanges() {
			const changes = {};

			this.models.forEach(model => {
				const percent = parseFloat(this.adjustments[model]);
				// the form shows percentages, the API stores fractions (0.2 = +20%)
				if (percent !== this.storedPercent(model)) changes[model] = Number((percent / 100).toFixed(4));
			});

			return changes;
		},
		// only the models whose geometry actually changed, and within those only the changed keys
		buildGeometryChanges() {
			const changes = {};

			if (!this.hasGeometry) return changes;

			this.models.forEach(model => {
				if (!this.geometryByCode[model]) return;

				const geometry = {};
				const widths = parseWidths(this.slatWidths[model]);
				const minGap = parseFloat(this.minGaps[model]);

				if (widths.join(',') !== this.storedWidths(model).join(',')) geometry.slatWidths = widths;
				if (minGap !== this.storedMinGap(model)) geometry.minGap = minGap;

				if (Object.keys(geometry).length > 0) changes[model] = geometry;
			});

			return changes;
		},
		async save() {
			const valid = await this.$refs.formModelAdjustments.validate();

			if (!valid) return;

			const adjustments = this.buildChanges();
			const geometry = this.buildGeometryChanges();

			if (Object.keys(adjustments).length > 0) {
				await this.dispatchSection('saveModelAdjustments', adjustments);
			}
			if (Object.keys(geometry).length > 0) {
				await this.dispatchSection('saveModelGeometry', geometry);
			}
		}
	},
	watch: {
		modelAdjustments: {
			handler: 'syncFromStore',
			deep: true
		},
		storedModels: {
			handler: 'syncFromStore',
			deep: true
		}
	},
	created() {
		this.syncFromStore();
	}
};
</script>

<style scoped></style>
